import { ref } from 'vue'

// Lazy import to avoid pulling legacy graph until needed
let avatarEngineMod = null
async function getAvatarEngine() {
  if (!avatarEngineMod) {
    avatarEngineMod = await import('../../services/avatarEngine')
  }
  return avatarEngineMod.default || avatarEngineMod
}

export function useAvatarTransform() {
  const controller = ref({ stop: null, update: null })
  const running = ref(false)

  async function startTransformed(constraints = { audio: true, video: true }, options = {}) {
    const raw = await navigator.mediaDevices.getUserMedia(constraints)
    return await startFromRaw(raw, options)
  }

  async function startFromRaw(raw, options = {}) {
    try {
      const engine = await getAvatarEngine()
      await engine.init()
      const transformOpts = buildTransformOptions(options)
      const transformed = await engine.createTransformedStream(raw, transformOpts)
      controller.value.stop = transformed.stop
      controller.value.update = transformed.updateSettings
      running.value = true
      return transformed.stream
    } catch (e) {
      // Fallback to raw camera on failure
      console.warn('[AvatarTransform] falling back to raw camera', e?.message || e)
      running.value = false
      return raw
    }
  }

  function buildTransformOptions(opts = {}) {
    const base = { ...(opts || {}) }
    // Prefer worker composite where available
    if (typeof base.meshWorkerComposite === 'undefined') base.meshWorkerComposite = true
    // Provide layer visibility defaults if not supplied
    if (!base.layerVisibility) {
      base.layerVisibility = {
        background: true,
        camera: true,
        avatarFace: true,
        avatarEyes: true,
        avatarHair: false
      }
    }
    return base
  }

  async function stop() {
    try {
      controller.value.stop?.()
    } catch {}
    controller.value.stop = null
    controller.value.update = null
    running.value = false
  }

  async function updateSettings(next) {
    try {
      await controller.value.update?.(next || {})
    } catch {}
  }

  return { startTransformed, startFromRaw, stop, updateSettings, running }
}

export default { useAvatarTransform }
